import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, Gift, CheckCircle, Utensils } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import ModernSelect from './ModernSelect';

interface BookingFormData {
    name: string;
    email: string;
    phone: string;
    date: string;
    time: string;
    guests: number;
    message: string;
}

const BookingWidget = () => {
    const { t } = useLanguage();
    const [isLoading, setIsLoading] = useState(false);
    // const [isExpanded, setIsExpanded] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<BookingFormData>();

    const timeSlots = [
        '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
        '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
    ];

    const onSubmit = async (data: BookingFormData) => {
        setIsLoading(true);
        try {
            const response = await axios.post('/api/v1/reservations', {
                ...data,
                guests: parseInt(data.guests as any, 10),
                date: new Date(data.date).toISOString(),
            });

            toast.success(t('booking.success'));
            reset();
            // setIsExpanded(false);

            // You could also show the confirmation code
            if (response.data.confirmation_code) {
                toast.success(`Confirmation code: ${response.data.confirmation_code}`);
            }
        } catch (error: any) {
            console.error('Booking error:', error);
            toast.error(error.response?.data?.error || t('booking.error'));
        } finally {
            setIsLoading(false);
        }
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <section className="relative -mt-20 z-30">
            <div className="container mx-auto max-w-7xl container-padding">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/10 dark:to-accent-900/10 rounded-full -translate-y-32 translate-x-32 opacity-50"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-accent-50 to-secondary-50 dark:from-accent-900/10 dark:to-secondary-900/10 rounded-full translate-y-24 -translate-x-24 opacity-50"></div>

                        <div className="relative z-10">
                            {/* Form Header */}
                            <div className="text-center mb-10">
                                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                                    <Utensils className="w-4 h-4" />
                                    <span>{t('booking.quickReservation')}</span>
                                </div>
                                <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-gray-900 dark:text-white">
                                    {t('booking.titlePerfect')}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {t('booking.description')}
                                </p>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                                {/* Personal Information */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                        <User className="w-5 h-5 text-primary-600" />
                                        <span>{t('booking.personalInformation')}</span>
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('booking.fullName')} *
                                            </label>
                                            <input
                                                type="text"
                                                {...register('name', { required: t('booking.fullNameRequired') })}
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('booking.enterFullName')}
                                            />
                                            {errors.name && (
                                                <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                    <span>⚠️</span>
                                                    <span>{errors.name.message}</span>
                                                </p>
                                            )}
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('booking.emailAddress')} *
                                            </label>
                                            <input
                                                type="email"
                                                {...register('email', {
                                                    required: t('booking.emailRequired'),
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: t('booking.invalidEmail')
                                                    }
                                                })}
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('booking.emailPlaceholder')}
                                            />
                                            {errors.email && (
                                                <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                    <span>⚠️</span>
                                                    <span>{errors.email.message}</span>
                                                </p>
                                            )}
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('booking.phoneNumber')} *
                                            </label>
                                            <input
                                                type="tel"
                                                {...register('phone', { required: t('booking.phoneRequired') })}
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('booking.phonePlaceholder')}
                                            />
                                            {errors.phone && (
                                                <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                    <span>⚠️</span>
                                                    <span>{errors.phone.message}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Reservation Details */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                        <Calendar className="w-5 h-5 text-accent-600" />
                                        <span>{t('booking.reservationDetails')}</span>
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {/* Date */}
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('booking.preferredDate')} *
                                            </label>
                                            <input
                                                type="date"
                                                {...register('date', { required: t('booking.dateRequired') })}
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-300"
                                                min={getMinDate()}
                                            />
                                            {errors.date && (
                                                <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                    <span>⚠️</span>
                                                    <span>{errors.date.message}</span>
                                                </p>
                                            )}
                                        </div>

                                        {/* Time */}
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('booking.preferredTime')} *
                                            </label>
                                            <ModernSelect
                                                options={timeSlots.map((time) => ({
                                                    value: time,
                                                    label: `${time} ${time < '15:00' ? `(${t('booking.lunch')})` : `(${t('booking.dinner')})`}`
                                                }))}
                                                value={watch('time')}
                                                onChange={(value) => setValue('time', value)}
                                                placeholder={t('booking.selectTimeSlot')}
                                            />
                                            {errors.time && (
                                                <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                    <span>⚠️</span>
                                                    <span>{errors.time.message}</span>
                                                </p>
                                            )}
                                        </div>

                                        {/* Number of Guests */}
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('booking.form.numberOfGuests')} *
                                            </label>
                                            <ModernSelect
                                                options={Array.from({ length: 20 }, (_, i) => i + 1).map((num) => ({
                                                    value: num.toString(),
                                                    label: `${num} ${num === 1 ? t('booking.form.guest') : t('booking.form.guests')}`
                                                }))}
                                                value={watch('guests')?.toString()}
                                                onChange={(value) => setValue('guests', parseInt(value))}
                                                placeholder={t('booking.form.selectPartySize')}
                                            />
                                            {errors.guests && (
                                                <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                                                    <span>⚠️</span>
                                                    <span>{errors.guests.message}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Special Requests */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                        <Gift className="w-5 h-5 text-secondary-600" />
                                        <span>{t('booking.form.specialRequests')}</span>
                                    </h3>
                                    <div>
                                        <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                            {t('booking.additionalInformation')}
                                        </label>
                                        <textarea
                                            {...register('message')}
                                            rows={5}
                                            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 resize-none"
                                            placeholder={t('booking.specialOccasionPlaceholder')}
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            {t('booking.examples')}
                                        </p>
                                    </div>
                                </div>

                                {/* Submit Section */}
                                <div className="border-t border-gray-200 dark:border-gray-600 pt-8">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            <span>{t('booking.confirmWithin2Hours')}</span>
                                        </div>
                                        <motion.button
                                            type="submit"
                                            disabled={isLoading}
                                            whileHover={!isLoading ? { scale: 1.05 } : {}}
                                            whileTap={!isLoading ? { scale: 0.95 } : {}}
                                            className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                                                    />
                                                    <span>{t('booking.submitting')}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Calendar className="w-5 h-5" />
                                                    <span>{t('booking.reserveTable')}</span>
                                                </>
                                            )}
                                        </motion.button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default BookingWidget;
